// Run job payload extensions for resuming and handing off
// (contract docs/plans/2026-10-02-checkpoints-contract.md §3.3). The worker's
// `RunJobPayloadSchema` (worker/src/types.ts) and the API's
// `ExecuteRunPayload` (api/src/services/run-dispatch.service.ts) both take
// these from here, so the two sides can't drift.

import { z } from 'zod'

/** Why a run is resumed — decides the resume turn's wording and is echoed on the `resumed` timeline event. */
export const ResumeViaSchema = z.enum([
  /** A decision on a paused run's `ask_human`/`request_approval`/guard-ask approval (plan 04 §2.6). */
  'approval_decision',
  /** `resume_after` of a quota-paused run passed and its connection is usable again. */
  'quota_wait',
  /** The worker holding the run died (lease expired); resumed from its last checkpoint. */
  'crash',
  /** A person pressed "Resume now" / "Resume at …" on a paused, failed or blocked run. */
  'manual',
])
export type ResumeVia = z.infer<typeof ResumeViaSchema>

/**
 * `payload.resume`: continue *this* run (same agent, same runner) from one of
 * its own checkpoints. A job still queued from before migration 0021 carries
 * `{pauseId}`; the preprocess maps it (checkpoint ids are the old pause ids —
 * the table was renamed, not copied).
 * TODO(checkpoints): drop the `pauseId` preprocess one release after 0020.
 */
export const RunJobResumeSchema = z.preprocess(
  (value) => {
    if (value && typeof value === 'object' && 'pauseId' in value && !('checkpointId' in value)) {
      const { pauseId, ...rest } = value as { pauseId: unknown } & Record<string, unknown>
      return { ...rest, checkpointId: pauseId, via: 'approval_decision' }
    }
    return value
  },
  z.object({
    checkpointId: z.string().uuid(),
    via: ResumeViaSchema.default('approval_decision'),
  }),
)
export type RunJobResume = z.infer<typeof RunJobResumeSchema>

/** Why a run was handed to another agent. */
export const HandoffReasonSchema = z.enum(['quota', 'manual'])
export type HandoffReason = z.infer<typeof HandoffReasonSchema>

/**
 * `payload.handoff`: this run is a *new* run (`parent_run_id` = `fromRunId`,
 * `attempt` + 1) on another agent that takes over the previous run's work.
 * The worker restores `checkpointId`'s workspace patch (never its session —
 * transcripts don't cross runners), writes the transcript file and builds the
 * handoff prompt (package 4). Never set together with `resume`.
 */
export const RunJobHandoffSchema = z.object({
  fromRunId: z.string().uuid(),
  /** The checkpoint to take over (usually relabelled `handoff`); null when the previous run never started (queued with an exhausted connection) — start fresh, just with the handoff note. */
  checkpointId: z.string().uuid().nullable(),
  fromAgent: z.object({
    id: z.string().uuid(),
    name: z.string().min(1),
    model: z.string().nullable(),
    runner: z.string().min(1),
  }),
  reason: HandoffReasonSchema,
  /** The previous run's failure message (e.g. "Claude usage limit reached · resets 3pm"), for the prompt. */
  failureMessage: z.string().max(2000).optional(),
  /** Every earlier run of the chain, oldest first, ending with `fromRunId` — the transcript file covers them all. */
  chainRunIds: z.array(z.string().uuid()).min(1),
  /** 1 for the first handoff of the chain; the API refuses past `onQuota.maxHandoffs` (manual handoffs count too). */
  handoffNumber: z.number().int().min(1),
  /**
   * What the chain before this run already used. The API has already
   * subtracted it from `payload.budget` (budget carries over the chain); this
   * is for the prompt/timeline only.
   */
  chainUsage: z
    .object({
      modelCalls: z.number().int().nonnegative(),
      tokens: z.number().int().nonnegative(),
      costUsd: z.number().nonnegative().optional(),
    })
    .default({ modelCalls: 0, tokens: 0 }),
})
export type RunJobHandoff = z.infer<typeof RunJobHandoffSchema>
